import { useDroppable } from "@dnd-kit/core";
import { Box } from "@mui/material";

import { AdminRegionLocationResDto } from "service/api/estabrookSchemas";
import { makeSxStyles } from "ui";
import useRegionManagementStore from "../../../state/region-management-store";
import DraggableOrder from "../route-planner/dnd/draggable-order";
import { ClickedAddress } from "../../../shared/hooks/use-address-click-handler";
import { RouteStatus } from "../route-planner/helpers/route-status.enum";
import { ZERO_SNOW_AMOUNT } from "../../../utils/measurement";

export interface RegionAddressesDroppableContainerDetails {
  id: string;
  items: AdminRegionLocationResDto[];
  color: string;
}

export interface RegionAddressesDroppableContainerProps {
  dropZoneDetails: RegionAddressesDroppableContainerDetails;
  onClickedAddress: (value: ClickedAddress) => void;
  handleMouseEnter: (orderId: string, event: React.MouseEvent) => void;
  handleMouseLeave: () => void;
  isLastItem: boolean;
  isPlanned?: boolean;
}

const RegionAddressesDroppableContainer: React.FC<
  RegionAddressesDroppableContainerProps
> = ({
  dropZoneDetails,
  handleMouseEnter,
  handleMouseLeave,
  onClickedAddress,
  isLastItem,
  isPlanned = false,
}) => {
  const { id, items, color } = dropZoneDetails;
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: {
      regionId: id,
      type: "container",
    },
  });
  const { selectedAddresses } = useRegionManagementStore();
  const styles = useStyles();

  if (!items?.length) return;

  return (
    <Box sx={styles.dropZone} data-over={isOver}>
      <Box ref={setNodeRef} sx={styles.itemsContainer}>
        {items.map((item, index) => {
          // Create mock order object for region management with service time
          // Based on user feedback, try to access serviceTime from item.location.serviceTime
          // Fall back to the schema-defined path if needed
          const serviceTime =
            (item as any)?.location?.serviceTime ??
            item?.locationServiceTime?.serviceTime;

          // Use the actual serviceTime value (even if 0) or default to 15 if undefined/null
          const finalServiceTime =
            typeof serviceTime === "number" ? serviceTime : 15;

          const mockOrder = {
            locationId: item?.locationId || `item-${index}`,
            locationSequence: item?.locationSequence || 0,
            serviceTime: finalServiceTime,
            status: "regular" as const,
            type: "onDemand" as const,
            snowAmount: ZERO_SNOW_AMOUNT,
          };

          // Get next location for travel time between locations
          const nextLocation =
            index < items.length - 1 ? items[index + 1] : undefined;

          // Create a mock next location object that matches AdminGetRoutesLocationsResDto structure
          const mockNextLocation = nextLocation
            ? ({
                locationId: nextLocation.locationId,
                locationSequence: nextLocation.locationSequence,
                serviceTime: nextLocation.locationServiceTime?.serviceTime,
                travelDetails: {
                  travelTimeBetweenLocations: nextLocation.timeBetweenLocations,
                },
              } as any)
            : undefined;

          return (
            <DraggableOrder
              key={item?.locationId || `item-${index}`}
              id={item?.locationId || `item-${index}`}
              regionId={id}
              color={color || "stone"}
              order={mockOrder}
              selected={selectedAddresses.some(
                (selected) => selected.id === item?.locationId,
              )}
              locationSequence={item?.locationSequence || 0}
              travelTimeFromStartingPoint={
                index === 0 ? item?.timeBetweenLocations : undefined
              }
              nextLocation={mockNextLocation}
              nextLocationId={nextLocation?.locationId}
              onClickedAddress={onClickedAddress}
              handleMouseEnter={handleMouseEnter}
              handleMouseLeave={handleMouseLeave}
              isLastItem={index === items.length - 1 && isLastItem}
              currentRouteStatus={
                isPlanned ? RouteStatus.Planned : RouteStatus.Planning
              }
              isFirstLocationToRender={index === 0}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const useStyles = makeSxStyles((theme) => ({
  dropZone: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: "20px",
    padding: "0",
    width: "100%",
    flexShrink: 0,
    "&:last-of-type": {
      borderRight: "none",
      marginRight: "0px",
    },
    "> svg": {
      marginRight: "8px",
    },
    "&[data-over='true']": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  itemsContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: "calc(100% - 23px)",
    borderRadius: "4px",
    // Removed transitions and hover effects that interfere with drag and tooltips
    // "&:hover": {
    //   backgroundColor: theme.palette.action.hover,
    // },
    // "& > div": {
    //   transition: "transform 0.2s ease",
    // },
  },
}));

export default RegionAddressesDroppableContainer;
